import React from 'react';

import CategorySelect from 'Components-redesign/Fields/CategorySelect';
import ColorSelect from 'Components-redesign/Fields/ColorSelect';
import { VAT_VALUES } from 'consts';
import { filterLanguages } from 'utils/langText';

import flagES from '../../assets/images/Flags/es.svg';
import flagEN from '../../assets/images/Flags/gb.svg';
import globe from '../../assets/images/Flags/globe.svg';
import flagSE from '../../assets/images/Flags/se.svg';
import FieldSelectList from '../Fields/FieldSelectList';
import ImageSelect from '../Fields/ImageSelect';
import LangInputFields from '../Fields/LangInputFields';
import MultiSelect from '../Fields/MultiSelect';
import PriceInput from '../Fields/PriceInput';
import Select from '../Fields/Select';
import TagsInput from '../Fields/TagsInput';
import Textarea from '../Fields/Textarea';
import TextInput from '../Fields/TextInput';
import TimezoneField from '../Fields/TimezoneField';
import Titlebar from '../Titlebar';
import Toggle from '../Toggle';

import { Field } from '.';

export enum InputTypes {
  TEXT = 'TEXT',
  TEXTLANG = 'TEXTLANG',
  INT = 'INT',
  SELECT = 'SELECT',
  SELECTCOLOR = 'SELECTCOLOR',
  SELECTVAT = 'SELECTVAT',
  CATEGORYSELECT = 'CATEGORYSELECT',
  MULTISELECT = 'MULTISELECT',
  TEXTAREA = 'TEXTAREA',
  TEXTAREALANG = 'TEXTAREALANG',
  PRICE = 'PRICE',
  HIDDEN = 'HIDDEN',
  TAGSINPUT = 'TAGSINPUT',
  IMAGESELECT = 'IMAGESELECT',
  TITLEBAR = 'TITLEBAR',
  TOGGLE = 'TOGGLE',
  TIMEZONE = 'TIMEZONE',
  FIELDSELECTLIST = 'FIELDSELECTLIST',
  PHONENUMBER = 'PHONENUMBER',
}

export const languageIcons = {
  se: flagSE,
  en: flagEN,
  es: flagES,
};

// Get field based on type
export const getField = (
  t: (str: string) => string,
  field: Field,
  formData,
  handleFieldChange,
  languages,
  isModalOpen?: boolean,
) => {
  switch (field.type) {
    case InputTypes.TEXT:
      return (
        <TextInput
          name={field.id}
          placeHolder={field.placeHolder ?? ''}
          value={formData[field.id].value ?? ''}
          handleChange={(e) => handleFieldChange(e.target.name, e.target.value)}
          maxLength={field.maxLength}
          disabled={field.disabled}
        />
      );

    case InputTypes.TEXTLANG:
      if (!languages) return;

      return (
        <LangInputFields
          languages={languages}
          name={field.id}
          placeholder={field.langPlaceHolder ?? undefined}
          value={formData[field.id].value}
          handleChange={(name, val, lang) => handleFieldChange(name, val, lang)}
        />
      );
    case InputTypes.PRICE:
      return (
        <PriceInput
          name={field.id}
          value={formData[field.id].value ?? ''}
          placeHolder={field.placeHolder ?? '0'}
          handleChange={(name, value) => handleFieldChange(name, value)}
        />
      );

    case InputTypes.TEXTAREA:
      return (
        <Textarea
          name={field.id}
          placeHolder={field.placeHolder ?? ''}
          value={formData[field.id].value ?? ''}
          handleChange={(e) => handleFieldChange(e.target.name, e.target.value)}
          maxLength={field.maxLength}
        />
      );

    case InputTypes.TEXTAREALANG: {
      if (!languages) return;

      const getLanguageIcon = (lang) => {
        const baseLanguages = ['se', 'en', 'es', 'no'];

        const companyHasNewLanguages = languages.some(
          (language) => !baseLanguages.includes(language),
        );

        if (lang === 'se') {
          return companyHasNewLanguages ? globe : flagSE;
        }
        return languageIcons[lang];
      };

      return filterLanguages(languages).map((lang) => {
        const value = formData[field.id].value ? formData[field.id].value[lang] : '';
        return (
          <Textarea
            key={lang}
            name={field.id}
            placeHolder={
              field.langPlaceHolder ? field.langPlaceHolder[lang] : t('General.EnterText')
            }
            value={value ?? ''}
            handleChange={(e) => handleFieldChange(e.target.name, e.target.value, lang)}
            maxLength={field.maxLength}>
            {languages.length > 1 && <img src={getLanguageIcon(lang)} />}
          </Textarea>
        );
      });
    }

    case InputTypes.INT:
      return (
        <TextInput
          name={field.id}
          type="number"
          placeHolder={field.placeHolder ?? ''}
          value={formData[field.id].value ?? ''}
          handleChange={(e) => handleFieldChange(e.target.name, e.target.value)}
          maxLength={field.maxLength}
        />
      );

    case InputTypes.SELECTCOLOR:
      return (
        <ColorSelect
          name={field.id}
          placeholder={field.placeHolder ?? t('General.ChooseColor')}
          selected={{ id: formData[field.id].value } ?? undefined}
          handleChange={(name, value) => handleFieldChange(name, value.id)}
        />
      );

    case InputTypes.SELECT:
      return (
        <Select
          name={field.id}
          placeholder={field.placeHolder ?? t('General.Choose')}
          selected={{ id: formData[field.id].value?.id ?? formData[field.id].value } ?? ''}
          handleChange={(name, value) => handleFieldChange(name, value)}
          list={field.list}
        />
      );

    case InputTypes.CATEGORYSELECT:
      return (
        <CategorySelect
          id={field.id}
          formData={formData[field.id]}
          list={field.list}
          placeholder={field.placeHolder ?? t('General.Choose')}
          handleChange={(name, value) => handleFieldChange(name, value)}
        />
      );

    case InputTypes.MULTISELECT:
      return (
        <MultiSelect
          name={field.id}
          placeholder={field.placeHolder ?? t('General.Choose')}
          selected={Array.isArray(field.value) ? field.value : formData[field.id].value ?? []}
          handleChange={(list) => handleFieldChange(field.id, list)}
          list={field.list}
        />
      );

    case InputTypes.SELECTVAT:
      return (
        <Select
          name={field.id}
          placeholder={field.placeHolder ?? t('General.ChooseVAT')}
          selected={{ id: formData[field.id].value ?? '' }}
          handleChange={(name, value) => handleFieldChange(name, value)}
          disabled={field.disabled}
          list={VAT_VALUES.map((value) => ({ id: value.toString(), name: `${value} %` }))}
        />
      );
    case InputTypes.TAGSINPUT:
      return (
        <TagsInput
          name={field.id}
          placeholder={field.placeHolder}
          items={formData[field.id].value}
          handleChange={(name, value) => handleFieldChange(name, value)}
        />
      );

    case InputTypes.IMAGESELECT:
      return (
        <ImageSelect
          name={field.id}
          currentImage={formData[field.id].value ?? ''}
          setImgIdForAdding={(name, value) => handleFieldChange(name, value)}
        />
      );

    case InputTypes.TITLEBAR:
      return <Titlebar title={formData[field.id].placeHolder} />;

    case InputTypes.TOGGLE:
      return (
        <Toggle
          value={formData[field.id].value}
          title={formData[field.id].placeHolder}
          handleChange={(value) => handleFieldChange(field.id, value)}
        />
      );

    case InputTypes.FIELDSELECTLIST:
      return (
        <FieldSelectList
          idToUpdate={formData[field.id].idToUpdate}
          formId={field.id}
          title={formData[field.id].label}
          description={formData[field.id].placeHolder}
          values={formData[field.id].value}
          list={formData[field.id].list}
          handleChange={(values) => handleFieldChange(field.id, values)}
          isModalOpen={field.id == 'itemsUsedOn' ? false : isModalOpen ?? false}
        />
      );

    case InputTypes.TIMEZONE:
      return (
        <TimezoneField
          name={field.id}
          selected={{ id: formData[field.id].value?.id ?? formData[field.id].value } ?? ''}
          handleChange={(name, value) => handleFieldChange(name, value)}
        />
      );

    case InputTypes.PHONENUMBER:
      return (
        <TextInput
          name={field.id}
          type="tel"
          placeHolder={field.placeHolder ?? ''}
          value={formData[field.id].value ?? ''}
          handleChange={(e) => handleFieldChange(e.target.name, e.target.value)}
          maxLength={field.maxLength}
        />
      );

    default:
      break;
  }
};
